import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, BadgeCheck, SlidersHorizontal } from "lucide-react";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { useToast } from "../../../hooks/useToast";
import { ToastContainer } from "../../../components/ui/Toast";
import { useAuth } from "../../../hooks/useAuth";
import { manageApprovalsApi } from "../../../lib/manageApprovalsApi";
import type { ApprovalControlRow } from "../../../lib/manageApprovalsApi";
import { APPROVAL_SCOPE_LABELS } from "../../../types/pages";
import { ApprovalControlModal } from "./ApprovalControlModal";

function countRestrictions(row: ApprovalControlRow): number {
  return Object.values(row.approvalDenies).reduce((sum, actions) => sum + (actions?.length ?? 0), 0);
}

export default function ManageApprovalsPage() {
  const { toasts, toast, dismissToast } = useToast();
  const { activeBusinessId, businesses } = useAuth();
  const [editing, setEditing] = useState<ApprovalControlRow | null>(null);

  const activeBusinessName = businesses.find((b) => b.businessId === activeBusinessId)?.name;

  const { data, isLoading, error } = useQuery({
    queryKey: ["approval-controls", activeBusinessId],
    queryFn: () => manageApprovalsApi.list(activeBusinessId!),
    enabled: !!activeBusinessId,
    // Rows derive from role pages + memberships, which can change on other pages.
    // Always refetch on navigation so edits (e.g. granting a role a new page) show
    // without a manual browser refresh.
    staleTime: 0,
    refetchOnMount: "always",
  });

  const rows = data?.items ?? [];

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-[#0f172a]">Manage Approvals</h1>
        {activeBusinessId ? (
          <div className="mt-1 flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-[#2447d9]" />
            <span className="text-sm text-[#64748b]">
              Approvers for <span className="font-medium text-[#2447d9]">{activeBusinessName ?? activeBusinessId}</span>
            </span>
          </div>
        ) : (
          <p className="mt-0.5 text-sm text-[#64748b]">
            Control which members may approve, reject, or release requests. Members keep full rights
            unless you remove specific actions here.
          </p>
        )}
      </div>

      {/* Content */}
      {!activeBusinessId ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#e2e8f0] bg-white py-16 text-center">
          <Building2 className="h-10 w-10 text-[#cbd5e1] mb-3" />
          <p className="text-sm font-medium text-[#64748b]">Select a business first</p>
          <p className="text-xs text-[#94a3b8] mt-1">Use the switcher in the top bar to choose a business.</p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-[#fca5a5] bg-[#fef2f2] p-4 text-sm text-[#dc2626]">
          Failed to load approvers. Please try again.
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#e2e8f0] bg-white py-16 text-center">
          <BadgeCheck className="h-10 w-10 text-[#cbd5e1] mb-3" />
          <p className="text-sm font-medium text-[#64748b]">No configurable approvers</p>
          <p className="text-xs text-[#94a3b8] mt-1">
            Only members with a custom role that grants an approval page appear here. Business admins
            always keep full approval rights.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#e2e8f0] bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[#f1f5f9] bg-[#f8fafc] text-[11px] uppercase tracking-wide text-[#94a3b8]">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Approval access</th>
                <th className="px-4 py-3 font-semibold text-right">Restrictions</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f5f9]">
              {rows.map((row) => {
                const restrictions = countRestrictions(row);
                return (
                  <tr key={row.userId} className="hover:bg-[#f8fafc]">
                    <td className="px-4 py-3 font-medium text-[#0f172a]">{row.name}</td>
                    <td className="px-4 py-3 text-[#64748b]">{row.roleName ?? "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {row.scopes.map((s) => (
                          <span key={s} className="inline-block rounded-full bg-[#f1f5f9] px-2 py-0.5 text-[11px] text-[#475569]">
                            {APPROVAL_SCOPE_LABELS[s]}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {restrictions === 0 ? (
                        <span className="text-[11px] font-medium text-[#16a34a]">Full rights</span>
                      ) : (
                        <span className="inline-block rounded-full bg-[#fef2f2] px-2 py-0.5 text-[11px] font-medium text-[#dc2626]">
                          {restrictions} removed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setEditing(row)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-1.5 text-xs font-medium text-[#475569] hover:bg-[#f1f5f9] hover:text-[#2447d9] transition-colors"
                      >
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                        Configure
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && activeBusinessId && (
        <ApprovalControlModal
          businessId={activeBusinessId}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast.success("Approval settings saved");
            setEditing(null);
          }}
          onError={() => toast.error("Failed to save approval settings")}
        />
      )}
    </div>
  );
}
